import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import _ from 'lodash';
import { Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faEye } from '@fortawesome/free-solid-svg-icons';
import { getData } from '../../../redux-store/actions';
import {
  GP_GET_LIST,
  POL_GET_MONEDAS,
  POL_GET_TIPOS_COBERTURA,
} from '../../../redux-store/constants/action-types';
import { ALL_OPTIONS, API, BASE_URL, SEARCH_DELAY_MS } from '../../../CONST';
import {
  cellDateOperation,
  cellDobleRowOperation,
  cellNumOperation,
  cellOperation,
  headerDoubleRowOperation,
  headerOperation,
  renderPolicyIsVigente,
} from '../../../helpers/tables';
import { buildFilterParams, fileDownload, intermediaryAccess } from '../../../helpers/helpers';
import BaseTable from '../../shared/BaseTable';
import FilterSelect from '../../shared/FilterSelect';
import { objectsToOptions } from '../../../helpers/selects';
import DownloadBtn from '../../shared/DownloadBtn';

const NO_FILTERED_ID = -1;

class GarantiaPolicyList extends Component {
  constructor(props) {
    super(props);
    this.filtering = false;
    this.filterUrl = '';
    this.fetchDataWithDebounce = _.debounce(this.fetchData, SEARCH_DELAY_MS);
    this.tableRef = React.createRef();
  }

  componentDidMount() {
    const { dispatch } = this.props;
    dispatch(
      getData({
        url: `${BASE_URL}${API.monedas}/`,
        type: POL_GET_MONEDAS,
      }),
    );
    dispatch(
      getData({
        url: `${BASE_URL}${API.tiposCobertura}/?tipo_poliza=garantia`,
        type: POL_GET_TIPOS_COBERTURA,
      }),
    );
  }

  componentDidUpdate(prevProps) {
    const { client: prevClient } = prevProps;
    const { client } = this.props;

    if (JSON.stringify(prevClient) !== JSON.stringify(client) && this.tableRef.current) {
      this.fetchStrategy(this.tableRef.current.state);
    }
  }

  onFilteredChange = () => {
    this.filtering = true;
  };

  fetchStrategy = (tableState) => {
    if (this.filtering) {
      return this.fetchDataWithDebounce(tableState);
    }
    return this.fetchData(tableState);
  };

  fetchData = (state) => {
    const { dispatch, user, client } = this.props;
    this.filtering = false;

    let filterParams = buildFilterParams(
      state,
      ['emision', 'balancegarantia__inicio_vigencia', 'balancegarantia__termino_vigencia'],
      ['vigentes'],
    );

    if (user.is_orsan && client) {
      filterParams = `${filterParams}client_id=${client.id}`;
    }

    if (intermediaryAccess(user) && client) {
      filterParams = `${filterParams}client_id=${client.id}&intermediario_id=${user.organization}`;
    }

    const filterUrl = `${BASE_URL}${API.garantias}/?${filterParams}`;

    // only fetch data if filters have changed
    if (filterUrl !== this.filterUrl) {
      this.filterUrl = filterUrl;

      dispatch(
        getData({
          url: filterUrl,
          type: GP_GET_LIST,
        }),
      );
    }
  };

  render() {
    const {
      garantiaPolicies,
      loading,
      loginIdToken,
      poliza: { monedas, tiposCobertura },
      user,
      client,
    } = this.props;

    if (monedas.length === 0 || tiposCobertura.length === 0) {
      return null;
    }

    const monedasOptions = objectsToOptions(monedas, 'nombre_corto', true);
    const tiposCoberturaOptions = objectsToOptions(tiposCobertura, 'descripcion', true);
    const xlsDownloadUrl = `${BASE_URL}${API.garantias}/download-xlsx`;
    const pdfDownloadUrl = `${BASE_URL}${API.garantias}/download-pdf`;
    const columns = [
      {
        Header: headerDoubleRowOperation('Número de', 'Póliza'),
        id: 'base__nemotecnico__icontains',
        accessor: (i) => cellOperation(i.nemotecnico),
        minWidth: 100,
        sortable: false,
      },
      {
        Header: headerOperation('Vigente'),
        id: 'vigentes',
        accessor: (i) => cellOperation(renderPolicyIsVigente(i.balancegarantia)),
        Filter: (obj) => (
          <FilterSelect
            onFilterChange={obj.onChange}
            filter={obj.filter}
            options={[{ label: 'Sí', value: '1' }, { label: 'No', value: '0' }]}
            defaultOption={{ label: 'Todas', value: ALL_OPTIONS }}
          />
        ),
        minWidth: 90,
        sortable: false,
      },
      {
        Header: headerOperation('Asegurado'),
        id: 'base__asegurado',
        accessor: (i) => cellDobleRowOperation(i.asegurado.rut, i.asegurado.nombre),
        minWidth: 200,
        sortable: false,
      },
      {
        Header: headerOperation('Cobertura'),
        id: 'base__tipo_cobertura_id',
        accessor: (i) => cellOperation(i.tipo_cobertura, 'left'),
        Filter: (obj) => (
          <FilterSelect
            filter={obj.filter}
            defaultOption={{ label: 'Todos', value: NO_FILTERED_ID }}
            onFilterChange={obj.onChange}
            options={tiposCoberturaOptions}
          />
        ),
        minWidth: 200,
        sortable: false,
      },
      {
        Header: headerOperation('Moneda'),
        id: 'base__moneda_asegurada_id',
        accessor: (i) => cellOperation(i.moneda),
        Filter: (obj) => (
          <FilterSelect
            onFilterChange={obj.onChange}
            filter={obj.filter}
            options={monedasOptions}
            defaultOption={{ label: 'Todas', value: NO_FILTERED_ID }}
          />
        ),
        sortable: false,
        minWidth: 100,
      },
      {
        Header: headerDoubleRowOperation('Monto', 'Garantizado'),
        id: 'cobertura',
        accessor: (i) => cellNumOperation(i.cobertura),
        minWidth: 100,
        filterable: false,
        sortable: false,
      },
      {
        Header: headerOperation('Prima Neta'),
        id: 'prima',
        accessor: (i) => cellNumOperation(i.prima, 'float'),
        minWidth: 100,
        filterable: false,
        sortable: false,
      },
      {
        Header: headerDoubleRowOperation('Fecha', 'Emisión'),
        id: 'emision',
        accessor: (i) => cellDateOperation(i.emision),
        sortable: false,
        minWidth: 100,
      },
      {
        Header: headerDoubleRowOperation('Inicio', 'Cobertura'),
        id: 'balancegarantia__inicio_vigencia',
        accessor: (i) => cellDateOperation(i.inicio_vigencia),
        sortable: false,
        minWidth: 100,
      },
      {
        Header: headerDoubleRowOperation('Fin', 'Cobertura'),
        id: 'balancegarantia__termino_vigencia',
        accessor: (i) => cellDateOperation(i.termino_vigencia),
        sortable: false,
        minWidth: 100,
      },
      {
        Header: headerDoubleRowOperation('Código de', 'Verificación'),
        id: 'codigo_verificacion',
        accessor: (i) => cellOperation(i.codigo_verificacion),
        sortable: false,
        filterable: false,
        minWidth: 120,
      },
      {
        Header: headerOperation('Acciones'),
        id: 'actions',
        accessor: (i) => (
          <div className="table-cell" style={{ textAlign: 'center' }}>
            <span title="ver">
              <Link
                to={{
                  pathname: `/asegurado/polizas-garantia/${i.id}`,
                  state: { returnTo: '/asegurado/polizas-garantia' },
                }}
                className="table-link policy"
              >
                <FontAwesomeIcon icon={faEye} />
              </Link>
            </span>
            &nbsp;&nbsp;
            <span title="descargar">
              <Button
                className="download"
                style={{ verticalAlign: 'baseline' }}
                onClick={() =>
                  fileDownload(`${BASE_URL}${API.garantias}/${i.id}/download-endoso`, loginIdToken)
                }
              >
                <FontAwesomeIcon icon={faDownload} />
              </Button>
            </span>
          </div>
        ),
        filterable: false,
        sortable: false,
        minWidth: 90,
      },
      {
        Header: headerOperation('Corredor'),
        id: 'corredor',
        accessor: 'balancegarantia.intermediario',
        Cell: (row) => cellOperation(row.value ? row.value.nombre : '', 'left'),
        sortable: false,
        filterable: false,
        minWidth: 160,
      },
      {
        Header: headerOperation('Contacto'),
        id: 'contacto',
        accessor: 'balancegarantia.intermediario',
        Cell: (row) => cellOperation(row.value ? row.value.phone : '', 'left'),
        sortable: false,
        filterable: false,
        minWidth: 120,
      },
    ];
    return (
      <>
        {!user.is_orsan && !intermediaryAccess(user) && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '65px auto',
              gridGap: '3px 8px',
              padding: '0 0 16px 16px',
            }}
          >
            <div className="orsan-label">Nombre:</div>
            <div className="orsan-value">{user.organization_name}</div>
            <div className="orsan-label">RUT:</div>
            <div className="orsan-value">{user.organization_rut}</div>
          </div>
        )}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto auto',
            gridGap: '10px',
            marginBottom: '6px',
          }}
        >
          <div style={{ gridColumnStart: 2 }}>
            <DownloadBtn
              id="xlsx-download"
              text="XLSX"
              tipText="Descargar Planilla Excel"
              url={client ? `${xlsDownloadUrl}?client_id=${client.id}` : xlsDownloadUrl}
            />
          </div>
          <div style={{ gridColumnStart: 3 }}>
            <DownloadBtn
              id="pdf-download"
              text="PDF"
              tipText="Descargar PDF"
              url={client ? `${pdfDownloadUrl}?client_id=${client.id}` : pdfDownloadUrl}
            />
          </div>
        </div>
        <BaseTable
          tableRef={this.tableRef}
          columns={columns}
          data={garantiaPolicies.data}
          filterable
          loading={loading}
          onFetchData={this.fetchStrategy}
          onFilteredChange={this.onFilteredChange}
          pages={garantiaPolicies.pages}
          resultCount={garantiaPolicies.count}
          showPagination
          style={{ maxHeight: '63vh' }}
        />
      </>
    );
  }
}

GarantiaPolicyList.propTypes = {
  dispatch: PropTypes.func.isRequired,
  garantiaPolicies: PropTypes.shape({
    data: PropTypes.array,
    pages: PropTypes.number,
    count: PropTypes.number,
  }).isRequired,
  poliza: PropTypes.shape({
    monedas: PropTypes.array,
    tiposCobertura: PropTypes.array,
  }).isRequired,
  user: PropTypes.shape({
    organization: PropTypes.number,
    organization_name: PropTypes.string,
    organization_rut: PropTypes.string,
    is_orsan: PropTypes.bool,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  loginIdToken: PropTypes.string.isRequired,
  client: PropTypes.shape({
    id: PropTypes.number,
  }),
};

GarantiaPolicyList.defaultProps = {
  client: null,
};

const mapStateToProps = (state) => ({
  garantiaPolicies: state.garantia.list.garantiaPolicies,
  loading: state.garantia.loading,
  poliza: state.poliza,
  user: state.login.user,
  loginIdToken: state.login.id_token,
});

export default connect(mapStateToProps)(GarantiaPolicyList);
